import L from "leaflet";
import "./canvasPin.css";

/** Leaflet が公開していない Canvas レンダラ・地図の内部プロパティ */
interface CanvasRendererInternals {
  _container?: HTMLCanvasElement;
  _ctx?: CanvasRenderingContext2D;
  _drawFirst: DrawOrder | null;
  _redrawBounds: L.Bounds | null;
  _drawing: boolean;
}

interface DrawableLayer {
  _pxBounds?: L.Bounds;
  _updatePath: () => void;
}

/** ピンの重ねる段（CanvasPinMarker.getDrawLevel）。ピン以外のレイヤーは 0 */
const drawLevelOf = (layer: L.Layer): number => {
  const level = (layer as Partial<{ getDrawLevel: () => number }>).getDrawLevel;
  return typeof level === "function" ? level.call(layer) : 0;
};

interface DrawOrder {
  layer: L.Layer;
  next: DrawOrder | null;
}

interface HitTestableLayer {
  _containsPoint: (point: L.Point) => boolean;
}

interface MapInternals {
  _fireDOMEvent: (e: Event, type: string, targets?: L.Layer[]) => void;
  _draggableMoved: (obj: unknown) => boolean;
  _animatingZoom?: boolean;
  dragging?: { moving: () => boolean };
}

/** ピンを横取りするクリック系のイベント */
const CLICK_EVENT_TYPES = ["click", "dblclick", "contextmenu"] as const;

/** ピンより手前に表示される Leaflet の要素（ポップアップ・ツールチップ・画像のマーカー） */
const ABOVE_PINS_SELECTOR =
  ".leaflet-popup, .leaflet-tooltip, .leaflet-marker-icon";

/** ペインの z-index。指定が無ければ null */
const paneZIndex = (pane: Element): number | null => {
  const zIndex = Number.parseInt(window.getComputedStyle(pane).zIndex, 10);
  return Number.isNaN(zIndex) ? null : zIndex;
};

/** ホバー判定の間引き間隔。Leaflet の Canvas レンダラと同じ値 */
const HOVER_THROTTLE_MS = 32;

/** ピンにホバー中、地図コンテナに付けてカーソルを指にするクラス */
export const CANVAS_PIN_HOVER_CLASS = "canvas-pin-hover";

/**
 * CanvasPinMarker を描くための Canvas レンダラ。
 *
 * Leaflet の既定の Canvas レンダラは canvas 要素自身でマウスイベントを受けるため、
 * マーカーペインに地図全面の canvas を置くと、その下にある MapLibre のレイヤー
 * （登記所備付地図・地価など）のクリックを全部奪ってしまう。
 * そこで canvas はイベントを透過させ、地図コンテナでキャプチャして
 * ピンに当たった時だけイベントを止めてピンに渡す。ピン以外の場所では何もしない。
 *
 * ピンは段（CanvasPinMarker.getDrawLevel）の小さい順に描き、同じ段の中では追加した順に描く。
 * 一覧に入った物件の青ピンや選択中のピンを、後から追加された他の種類のピンより上に出すため。
 */
export class CanvasPinRenderer extends L.Canvas {
  private hoveredLayer: L.Layer | null = null;

  private hoverThrottled = false;

  onAdd(map: L.Map): this {
    super.onAdd(map);

    const canvas = (this as unknown as CanvasRendererInternals)._container;
    if (canvas) canvas.style.pointerEvents = "none";

    const container = map.getContainer();
    CLICK_EVENT_TYPES.forEach((type) => {
      container.addEventListener(type, this.handleClick, true);
    });
    container.addEventListener("mousemove", this.handleMouseMove, true);
    container.addEventListener("mouseleave", this.handleMouseLeave);
    return this;
  }

  onRemove(map: L.Map): this {
    const container = map.getContainer();
    CLICK_EVENT_TYPES.forEach((type) => {
      container.removeEventListener(type, this.handleClick, true);
    });
    container.removeEventListener("mousemove", this.handleMouseMove, true);
    container.removeEventListener("mouseleave", this.handleMouseLeave);
    this.setHovered(null, null);
    return super.onRemove(map);
  }

  /**
   * イベントの対象がピンより手前に見えている要素かどうか。
   * 地図コンテナでキャプチャしているため、ポップアップのボタンや地図のコントロールなど、
   * ピンの上に重なった要素のクリックも先に受け取ってしまう。それらはピンに渡さない。
   */
  private isAbovePins(e: MouseEvent): boolean {
    const { target } = e;
    if (!(target instanceof Element) || !this._map) return false;
    if (target === this._map.getContainer()) return false;

    // コントロールや凡例など、地図の外枠に直接重ねた要素
    const mapPane = this._map.getPane("mapPane");
    if (!mapPane?.contains(target)) return true;

    if (target.closest(ABOVE_PINS_SELECTOR)) return true;

    // マーカーペインより上のペイン（役所資料のピンなど）にある要素
    const pane = target.closest(".leaflet-pane");
    const markerPane = this._map.getPane(this.options.pane ?? "markerPane");
    if (!pane || pane === mapPane || !markerPane) return false;
    const paneZ = paneZIndex(pane);
    const markerZ = paneZIndex(markerPane);
    return paneZ !== null && markerZ !== null && paneZ > markerZ;
  }

  // Leaflet の _draw と同じく再描画範囲に掛かるレイヤーを描くが、段の小さい順に描く
  _draw(): void {
    const internals = this as unknown as CanvasRendererInternals;
    const ctx = internals._ctx;
    if (!ctx) return;
    const bounds = internals._redrawBounds;

    const byLevel = new Map<number, DrawableLayer[]>();
    for (let order = internals._drawFirst; order; order = order.next) {
      const layer = order.layer as unknown as DrawableLayer;
      if (bounds && !layer._pxBounds?.intersects(bounds)) {
        continue;
      }
      const level = drawLevelOf(order.layer);
      const layers = byLevel.get(level) ?? [];
      layers.push(layer);
      byLevel.set(level, layers);
    }

    ctx.save();
    if (bounds?.min && bounds.max) {
      const size = bounds.getSize();
      ctx.beginPath();
      ctx.rect(bounds.min.x, bounds.min.y, size.x, size.y);
      ctx.clip();
    }
    internals._drawing = true;
    [...byLevel.keys()]
      .sort((a, b) => a - b)
      .forEach((level) => {
        byLevel.get(level)?.forEach((layer) => {
          layer._updatePath();
        });
      });
    internals._drawing = false;
    ctx.restore();
  }

  /** マウス位置にある最前面のピンを返す（段が上のもの、同じ段なら後に描いたものが上） */
  private hitTest(e: MouseEvent): L.Layer | null {
    if (this.isAbovePins(e)) return null;
    const point = this._map.mouseEventToLayerPoint(e);
    let hit: L.Layer | null = null;
    let hitLevel = -Infinity;
    for (
      let order = (this as unknown as CanvasRendererInternals)._drawFirst;
      order;
      order = order.next
    ) {
      const { layer } = order;
      const level = drawLevelOf(layer);
      if (
        level >= hitLevel &&
        (layer.options as L.InteractiveLayerOptions).interactive === true &&
        (layer as unknown as HitTestableLayer)._containsPoint(point)
      ) {
        hit = layer;
        hitLevel = level;
      }
    }
    return hit;
  }

  private readonly handleClick = (e: MouseEvent): void => {
    if (!this._map) return;
    const map = this._map as unknown as MapInternals;
    // 地図をドラッグした後の click はピンのクリックとして扱わない
    if (e.type === "click" && map._draggableMoved(this._map)) return;

    const layer = this.hitTest(e);
    if (!layer) return;

    // 下のレイヤー（MapLibre など）や地図自身のクリック処理に渡さない
    e.stopPropagation();
    map._fireDOMEvent(e, e.type, [layer]);
  };

  private readonly handleMouseMove = (e: MouseEvent): void => {
    if (!this._map || this.hoverThrottled) return;
    const map = this._map as unknown as MapInternals;
    if (map.dragging?.moving() === true || map._animatingZoom === true) return;

    this.setHovered(this.hitTest(e), e);

    this.hoverThrottled = true;
    window.setTimeout(() => {
      this.hoverThrottled = false;
    }, HOVER_THROTTLE_MS);
  };

  private readonly handleMouseLeave = (e: MouseEvent): void => {
    this.setHovered(null, e);
  };

  private setHovered(layer: L.Layer | null, e: MouseEvent | null): void {
    if (layer === this.hoveredLayer) return;
    const map = this._map as unknown as MapInternals | undefined;

    if (this.hoveredLayer && e && map) {
      map._fireDOMEvent(e, "mouseout", [this.hoveredLayer]);
    }
    this.hoveredLayer = layer;

    const container = this._map?.getContainer();
    if (layer) {
      container?.classList.add(CANVAS_PIN_HOVER_CLASS);
      if (e && map) map._fireDOMEvent(e, "mouseover", [layer]);
    } else {
      container?.classList.remove(CANVAS_PIN_HOVER_CLASS);
    }
  }
}
